import {useMutation} from '@apollo/client';
import {useNavigate} from 'react-router-dom';
import {useSnackbar} from 'notistack';
import {useAccountContext} from '../../../contexts/AccountContext';
import {
  UPSERT_BUILDER,
  UPSERT_BUILDER_MODIFY,
  UPSERT_BUILDER_PROTECTION,
  UPSERT_BUILDER_ANNUITY,
  UPSERT_BUILDER_CONTRIBUTE,
  UPSERT_BUILDER_WITHDRAW,
  UPSERT_BUILDER_TRANSFER,
  UPSERT_BUILDER_ACTION,
  UPSERT_BUILDER_CHARGE_AD_HOC_FEE,
  UPSERT_SET_UP_NEW,
} from '../BuilderGrid/builderOvPotQueries';
import {useReviewContext} from '../../../contexts/ReviewContext';
import {useBuilderContext} from '../CreateBuilder/useBuilderContext';
import {builderFieldsType} from '../CreateBuilder/builderFields';
import {
  UpsertOvbuilder,
  UpsertOvbuilderVariables,
} from '../../../schema-types/UpsertOvbuilder';
import {
  UpsertSetUpNew,
  UpsertSetUpNewVariables,
} from '../../../schema-types/UpsertSetUpNew';
import {
  UpsertBuilderModify,
  UpsertBuilderModifyVariables,
} from '../../../schema-types/UpsertBuilderModify';
import {
  UpsertBuilderProtection,
  UpsertBuilderProtectionVariables,
} from '../../../schema-types/UpsertBuilderProtection';
import {
  UpsertBuilderAnnuity,
  UpsertBuilderAnnuityVariables,
} from '../../../schema-types/UpsertBuilderAnnuity';
import {
  UpsertBuilderContribute,
  UpsertBuilderContributeVariables,
} from '../../../schema-types/UpsertBuilderContribute';
import {
  UpsertBuilderWithdraw,
  UpsertBuilderWithdrawVariables,
} from '../../../schema-types/UpsertBuilderWithdraw';
import {
  UpsertBuilderTransfer,
  UpsertBuilderTransferVariables,
} from '../../../schema-types/UpsertBuilderTransfer';
import {
  UpsertBuilderAction,
  UpsertBuilderActionVariables,
} from '../../../schema-types/UpsertBuilderAction';
import {
  UpsertBuilderChargeAdHocFee,
  UpsertBuilderChargeAdHocFeeVariables,
} from '../../../schema-types/UpsertBuilderChargeAdHocFee';
import {saveBuilder, createBuilderUpdateProps} from './saveBuilder';
import {useQueryParams} from '../../../hooks/useQueryParams';

export type {createBuilderUpdateProps};

export function useSaveBuilder(
  setSaving: (val: boolean) => void,
  type: builderFieldsType['how']
) {
  const {reviewId} = useReviewContext();
  const {account, accountId} = useAccountContext();
  const {builderDetails, setOvBuilderId} = useBuilderContext();
  const {enqueueSnackbar, closeSnackbar} = useSnackbar();
  const navigate = useNavigate();
  const query = useQueryParams();
  const existingOvrecommendId = query.get('ovrecommendId');

  const [upsertBuilder] = useMutation<UpsertOvbuilder, UpsertOvbuilderVariables>(
    UPSERT_BUILDER
  );
  const [upsertSetUpNew] = useMutation<UpsertSetUpNew, UpsertSetUpNewVariables>(
    UPSERT_SET_UP_NEW
  );
  const [upsertBuilderModify] = useMutation<
    UpsertBuilderModify,
    UpsertBuilderModifyVariables
  >(UPSERT_BUILDER_MODIFY);
  const [upsertBuilderProtection] = useMutation<
    UpsertBuilderProtection,
    UpsertBuilderProtectionVariables
  >(UPSERT_BUILDER_PROTECTION);
  const [upsertBuilderAnnuity] = useMutation<
    UpsertBuilderAnnuity,
    UpsertBuilderAnnuityVariables
  >(UPSERT_BUILDER_ANNUITY);
  const [upsertBuilderContribute] = useMutation<
    UpsertBuilderContribute,
    UpsertBuilderContributeVariables
  >(UPSERT_BUILDER_CONTRIBUTE);
  const [upsertBuilderWithdraw] = useMutation<
    UpsertBuilderWithdraw,
    UpsertBuilderWithdrawVariables
  >(UPSERT_BUILDER_WITHDRAW);
  const [upsertBuilderTransfer] = useMutation<
    UpsertBuilderTransfer,
    UpsertBuilderTransferVariables
  >(UPSERT_BUILDER_TRANSFER);
  const [upsertBuilderAction] = useMutation<
    UpsertBuilderAction,
    UpsertBuilderActionVariables
  >(UPSERT_BUILDER_ACTION);
  const [upsertBuilderChargeAdHocFee] = useMutation<
    UpsertBuilderChargeAdHocFee,
    UpsertBuilderChargeAdHocFeeVariables
  >(UPSERT_BUILDER_CHARGE_AD_HOC_FEE);

  return {
    createUpdate: (props: createBuilderUpdateProps) =>
      saveBuilder({
        accountId: account?.id || accountId,
        contacts: account?.contacts || [],
        builderDetails: builderDetails
          ? {...builderDetails, id: builderDetails?.id ?? existingOvrecommendId}
          : null,
        reviewId,
        setOvBuilderId,
        navigate,
        enqueueSnackbar,
        closeSnackbar,
        setSaving,
        type,
        mutations: {
          upsertBuilder,
          upsertSetUpNew,
          upsertBuilderModify,
          upsertBuilderProtection,
          upsertBuilderAnnuity,
          upsertBuilderContribute,
          upsertBuilderWithdraw,
          upsertBuilderTransfer,
          upsertBuilderAction,
          upsertBuilderChargeAdHocFee,
        },
        ...props,
      }),
  };
}
